import { asDateString } from './member-update-payload.util';

const STOCK_FIELDS = [
  'stockQuantityAdded',
  'stockQuantityRemoved',
  'locationId',
  'locationName',
  'totalQuantity',
] as const;

const PRODUCT_CREATE_FIELDS = [
  'name',
  'description',
  'category',
  'role',
  'barcode',
  'reOrderLevel',
  'reOrderQuantity',
  'cost',
  'imagePath',
  'remarks',
  'gstOnProduct',
  'normalPrice',
  'wholeSalePrice',
  'totalQuantity',
  'type',
  'unit',
] as const;

const PRODUCT_UPDATE_FIELDS = [...PRODUCT_CREATE_FIELDS, 'isActive'] as const;

function resolveCategory(value: unknown): string | undefined {
  if (value == null) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const o = value as Record<string, unknown>;
    if (o.code != null) return String(o.code);
    if (o._id != null) return String(o._id);
  }
  return undefined;
}

function assignProductFields(
  payload: Record<string, unknown>,
  product: Record<string, unknown>,
  fields: readonly string[],
): void {
  for (const key of fields) {
    const value = key === 'category'
      ? resolveCategory(product.category)
      : product[key];
    if (value === undefined || value === null) continue;
    if (typeof value === 'number' || typeof value === 'boolean') {
      payload[key] = value;
    } else {
      payload[key] = String(value);
    }
  }
}

function buildProductStockItem(raw: Record<string, unknown>): Record<string, unknown> {
  const item: Record<string, unknown> = {};
  for (const key of STOCK_FIELDS) {
    const value = raw[key];
    if (value === undefined || value === null) continue;
    if (typeof value === 'number') {
      item[key] = value;
    } else {
      item[key] = String(value);
    }
  }
  return item;
}

export interface ProductCreateOptions {
  company: string;
  branch: string;
}

export interface ProductCreateExtras {
  stocks?: object[];
  ingredients?: object[];
  createdOn?: Date | string;
}

/** Body for POST .../branches/:branchId/products (CreateProductDto). */
export function buildProductCreatePayload(
  product: object,
  options: ProductCreateOptions,
  extras: ProductCreateExtras = {},
): Record<string, unknown> {
  const p = (product || {}) as Record<string, unknown>;
  const payload: Record<string, unknown> = {
    company: options.company,
    branch: options.branch,
    name: String(p.name ?? ''),
  };

  assignProductFields(payload, p, PRODUCT_CREATE_FIELDS.filter((key) => key !== 'name'));

  const createdOn = asDateString(extras.createdOn ?? p.createdOn);
  if (createdOn) payload.createdOn = createdOn;

  const stocks = extras.stocks ?? p.stocks;
  if (Array.isArray(stocks) && stocks.length) {
    payload.stocks = stocks.map((s) => buildProductStockItem(s as Record<string, unknown>));
  }

  const ingredients = extras.ingredients ?? p.ingredients;
  if (Array.isArray(ingredients) && ingredients.length) {
    payload.ingredients = ingredients;
  }

  return payload;
}

export interface ProductUpdateExtras {
  stocks?: object[];
  ingredients?: object[];
  totalPayment?: number | string;
  paymentMode?: string;
}

/** Body for PATCH .../branches/:branchId/products/:id (UpdateProductDto). */
export function buildProductUpdatePayload(
  product: object,
  options: ProductCreateOptions,
  extras: ProductUpdateExtras = {},
): Record<string, unknown> {
  const p = (product || {}) as Record<string, unknown>;
  const payload: Record<string, unknown> = {
    company: options.company,
    branch: options.branch,
  };

  assignProductFields(payload, p, PRODUCT_UPDATE_FIELDS);

  const stocks = extras.stocks ?? p.stocks;
  if (Array.isArray(stocks) && stocks.length) {
    payload.stocks = stocks.map((s) => buildProductStockItem(s as Record<string, unknown>));
  }

  const ingredients = extras.ingredients ?? p.ingredients;
  if (Array.isArray(ingredients) && ingredients.length) {
    payload.ingredients = ingredients;
  }

  if (extras.totalPayment !== undefined && extras.totalPayment !== null) {
    payload.totalPayment = extras.totalPayment;
  }
  if (extras.paymentMode) payload.paymentMode = extras.paymentMode;

  return payload;
}

/** Body for POST .../branches/:branchId/products/actions/update-inventory-count. */
export function buildUpdateInventoryCountPayload(
  products: object[],
): { products: Record<string, unknown>[] } {
  return {
    products: (products || [])
      .map((raw) => {
        const p = raw as Record<string, unknown>;
        if (!p._id) return null;
        const item: Record<string, unknown> = { _id: String(p._id) };
        if (p.totalQuantity !== undefined && p.totalQuantity !== null) {
          item.totalQuantity = Number(p.totalQuantity);
        }
        if (p.countedQunatity !== undefined && p.countedQunatity !== null) {
          item.countedQunatity = Number(p.countedQunatity);
        }
        return item;
      })
      .filter((item): item is Record<string, unknown> => item != null),
  };
}
